import React from 'react';
import { saveAs } from 'file-saver';

/**
 * ExportCSV Component
 * A reusable component for exporting structured table data to CSV format
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of objects containing the data to export
 * @param {Array} props.columns - Array of column definitions with key and label properties
 * @param {string} props.filename - Filename for the downloaded CSV (without .csv extension)
 * @param {Array} props.visibleColumns - Optional array of column keys to include (if not provided, all columns are included)
 * @param {Object} props.options - Additional CSV generation options
 * @param {string} props.options.delimiter - CSV delimiter (default: ',')
 * @param {boolean} props.options.includeHeaders - Whether to include column headers (default: true)
 * @param {string} props.options.encoding - File encoding (default: 'utf-8')
 * @param {boolean} props.options.escapeQuotes - Whether to escape quotes in data (default: true)
 * @param {Function} props.onSuccess - Callback function called on successful export
 * @param {Function} props.onError - Callback function called on export error
 * @param {boolean} props.disabled - Whether the export is disabled
 * @param {string} props.buttonText - Custom text for the export button
 * @param {string} props.buttonClassName - Custom CSS class for the button
 */
const ExportCSV = ({
  data = [],
  columns = [],
  filename = 'export',
  visibleColumns = null,
  options = {},
  onSuccess = () => {},
  onError = () => {},
  disabled = false,
  buttonText = 'Export CSV',
  buttonClassName = ''
}) => {
  // Default options
  const defaultOptions = {
    delimiter: ',',
    includeHeaders: true,
    encoding: 'utf-8',
    escapeQuotes: true
  };

  const finalOptions = { ...defaultOptions, ...options };

  // Validate dependencies
  const checkDependencies = () => {
    if (typeof saveAs === 'undefined') {
      throw new Error('file-saver is not available. Please ensure file-saver is installed.');
    }
  };

  // Validate input data
  const validateInput = () => {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Data must be a non-empty array.');
    }
    if (!Array.isArray(columns) || columns.length === 0) {
      throw new Error('Columns must be a non-empty array.');
    }
    if (!filename || typeof filename !== 'string') {
      throw new Error('Filename must be a non-empty string.');
    }
  };

  // Filter columns to only include visible ones if specified
  const getFilteredColumns = () => {
    if (!visibleColumns) return columns;
    
    return columns.filter(col => 
      visibleColumns.includes(col.key) || visibleColumns.includes(col.label)
    );
  };

  // Escape CSV value to prevent corruption
  const escapeCSVValue = (value) => {
    if (value === null || value === undefined) {
      return ''; // Empty string for null/undefined values
    }

    // Convert to string
    const stringValue = String(value);

    // Check if value needs to be wrapped in quotes
    const needsQuotes = stringValue.includes(finalOptions.delimiter) || 
                       stringValue.includes('"') || 
                       stringValue.includes('\n') || 
                       stringValue.includes('\r') ||
                       stringValue.includes('\t');

    if (needsQuotes) {
      if (finalOptions.escapeQuotes) {
        // Escape quotes by doubling them
        const escapedValue = stringValue.replace(/"/g, '""');
        return `"${escapedValue}"`;
      } else {
        // Just wrap in quotes without escaping
        return `"${stringValue}"`;
      }
    }

    return stringValue;
  };

  // Prepare CSV data - export exactly what's provided without modification
  const prepareCSVData = (filteredColumns) => {
    const csvRows = [];

    // Add headers if enabled
    if (finalOptions.includeHeaders) {
      const headerRow = filteredColumns.map(col => col.label);
      csvRows.push(headerRow.join(finalOptions.delimiter));
    }

    // Add data rows
    data.forEach(row => {
      const csvRow = filteredColumns.map(col => {
        const value = row[col.key];
        
        // Handle different data types without corruption
        if (value === null || value === undefined) {
          return ''; // Empty string for null/undefined values
        }
        
        if (Array.isArray(value)) {
          return escapeCSVValue(value.join(', ')); // Join arrays with comma and space
        }
        
        if (typeof value === 'object') {
          try {
            return escapeCSVValue(JSON.stringify(value)); // Convert objects to JSON string
          } catch (error) {
            return escapeCSVValue(String(value)); // Fallback to string conversion
          }
        }
        
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No'; // Convert booleans to readable text
        }
        
        if (typeof value === 'number') {
          return value.toString(); // Convert numbers to string
        }
        
        return escapeCSVValue(String(value)); // Convert everything else to string
      });
      
      csvRows.push(csvRow.join(finalOptions.delimiter));
    });

    return csvRows.join('\n');
  };

  // Generate and download CSV
  const generateCSV = async () => {
    try {
      // Validate inputs and dependencies
      validateInput();
      checkDependencies();

      // Filter columns
      const filteredColumns = getFilteredColumns();
      
      // Prepare CSV data
      const csvContent = prepareCSVData(filteredColumns);
      
      // Create BOM for proper UTF-8 encoding in Excel
      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csvContent;
      
      // Create blob with proper MIME type
      const blob = new Blob([csvWithBOM], { 
        type: `text/csv;charset=${finalOptions.encoding};` 
      });
      
      // Generate filename with timestamp if not provided
      let finalFilename = filename;
      if (!finalFilename.includes('.')) {
        const timestamp = new Date().toISOString().split('T')[0];
        finalFilename = `${filename}_${timestamp}.csv`;
      } else if (!finalFilename.endsWith('.csv')) {
        finalFilename = `${filename}.csv`;
      }
      
      // Download file
      saveAs(blob, finalFilename);
      
      // Call success callback
      onSuccess({
        format: 'CSV',
        filename: finalFilename,
        dataCount: data.length,
        columnCount: filteredColumns.length,
        csvContent: csvContent
      });
      
    } catch (error) {
      console.error('CSV export failed:', error);
      
      // Call error callback
      onError({
        format: 'CSV',
        error: error.message,
        details: error
      });
      
      // Show user-friendly error message
      alert(`CSV export failed: ${error.message}`);
    }
  };

  // Handle button click
  const handleExport = (event) => {
    event.preventDefault();
    if (!disabled) {
      generateCSV();
    }
  };

  return (
    <button
      className={`export-csv-button ${buttonClassName}`}
      onClick={handleExport}
      disabled={disabled}
      style={{
        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
        color: '#134b5f',
        border: '1.5px solid #7aa7b6',
        borderRadius: '14px',
        padding: '10px 16px',
        fontWeight: 600,
        fontSize: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
          e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.target.style.transform = 'translateY(0)';
          e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
          e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
        }
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
      {buttonText}
    </button>
  );
};

export default ExportCSV;
